'use client';
import { prepareCaptchaBody } from '@eliadmin/core/src/api/recaptcha-client';
import { CaptchaAction } from '@eliadmin/core/src/api/recaptcha-actions';
import { CaptchaFormMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import {
  createAdvisory,
  fetchAdvisoryCategories,
} from '@eliadmin/core/src/api/advisories/advisories-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useDialogStore,
} from '@eliadmin/core/src/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { MultiSelect } from '@eliadmin/core/src/components/ui/multi-select';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { Textarea } from '@eliadmin/core/src/components/ui/textarea';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { FiSend } from 'react-icons/fi';
import { z } from 'zod';

export const addAdvisorySchema = (VM: FormValidationMessages) =>
  z
    .object({
      signed_in: z.boolean(),
      email: z.string().optional(), // Email is optional by default
      name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      value: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      categories: z
        .array(
          z.object({
            label: z.string(),
            value: z.number(),
          }),
        )
        .optional(),
    })
    .superRefine((data, ctx) => {
      if (!data.signed_in) {
        // Enforce email requirement if the user is not signed in
        if (!data.email || data.email.trim() === '') {
          ctx.addIssue({
            path: ['email'],
            message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
            code: z.ZodIssueCode.custom,
          });
        }
      }
    });

export function AddAdvisoryDialog() {
  const close = useDialogStore(s => s.close);

  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const router = useRouter();

  const { toast } = useToast();

  const locale = useLocaleStore(s => s.currentLocale);

  const me = useCustomerStore(s => s.me);

  const form = useForm<z.infer<ReturnType<typeof addAdvisorySchema>>>({
    defaultValues: {
      signed_in: !!me,
      email: me?.customer.email,
      value: '',
      name: '',
      categories: [],
    },
    resolver: zodResolver(addAdvisorySchema(formValidationMessages)),
  });

  const queryClient = useQueryClient();
  const createAdvisoryMutation = useMutation({ mutationFn: createAdvisory });

  const { data: categories } = useQuery({
    queryKey: [CacheKey.ADVISORIES_CATEGORIES],
    queryFn: () => fetchAdvisoryCategories({ locale }),
  });

  const categoriesOptions = categories?.data.map(category => ({
    label: category.name,
    value: category.id,
  }));

  const handleSubmit = form.handleSubmit(async data => {
    // if (!me?.authToken) return;

    const { categories, ...restAll } = data;
    const rest = {
      email: restAll.email,
      name: restAll.name,
      value: restAll.value,
    };
    const categoriesIds = categories?.map(category => ({
      id: category.value,
    }));

    try {
      await createAdvisoryMutation.mutateAsync({
        locale,
        body: await prepareCaptchaBody({ ...rest, ...(categoriesIds ? { categories: categoriesIds } : {}) }, CaptchaAction.ADVISORY),
        authToken: me?.authToken || '',
      });
      router.refresh();
      close(AddAdvisoryDialog.id);
      queryClient.invalidateQueries({ queryKey: [CacheKey.ADVISORIES] });
      toast({
        title: t('text1787'),
        variant: 'success',
      });
    } catch (e) {
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text594'),
        description: (e as any).message,
        variant: 'destructive',
      });
    }
  });

  return (
    <DialogContent>
      <form onSubmit={handleSubmit} className={cn('flex flex-col gap-6', 'ea-add-advisory-dialog')}>
        <Form {...form}>
          <CaptchaFormMessage errors={form.formState.errors} />
          <DialogHeader className={cn('ea-add-advisory-dialog__header')}>
            <DialogTitle className={cn('font-semibold', 'ea-add-advisory-dialog__header-title')}>
              {t('text635')}
            </DialogTitle>
          </DialogHeader>

          <Separator className={cn('ea-add-advisory-dialog__separator')} />

          <div className="grid grid-cols-2 gap-2 @container">
            {!form.getValues('signed_in') && (
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem
                    required
                    className={cn('col-span-2', 'ea-add-advisory-dialog__name-field')}
                  >
                    <FormLabel className={cn('', 'ea-add-advisory-dialog__name-label')}>
                      {t('text35')}
                    </FormLabel>
                    <FormControl className={cn('', 'ea-add-advisory-dialog__name-input')}>
                      <Input {...field} placeholder={t('text35')} />
                    </FormControl>
                    <FormMessage className={cn('', 'ea-add-advisory-dialog__name-message')} />
                  </FormItem>
                )}
              />
            )}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem
                  required
                  className={cn('col-span-2', 'ea-add-advisory-dialog__name-field')}
                >
                  <FormLabel className={cn('', 'ea-add-advisory-dialog__name-label')}>
                    {t('text552')}
                  </FormLabel>
                  <FormControl className={cn('', 'ea-add-advisory-dialog__name-input')}>
                    <Input {...field} placeholder={t('text552')} />
                  </FormControl>
                  <FormMessage className={cn('', 'ea-add-advisory-dialog__name-message')} />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="categories"
              render={({ field }) => {
                return (
                  <FormItem className={cn('col-span-2', 'ea-add-advisory-dialog__category-field')}>
                    <FormLabel>{t('text176')}</FormLabel>
                    <FormControl>
                      <MultiSelect
                        options={categoriesOptions}
                        placeholder={t('text478')}
                        noOptionsMessage={() => t('text477')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            <FormField
              control={form.control}
              name="value"
              render={({ field }) => (
                <FormItem
                  className={cn('col-span-2 mt-1', 'ea-add-advisory-dialog__value-field')}
                  required
                >
                  {/* <FormLabel className={cn('', 'ea-add-advisory-dialog__value-label')}>
                    {t('text551')}
                  </FormLabel> */}
                  <FormControl className={cn('', 'ea-add-advisory-dialog__value-textarea')}>
                    <Textarea {...field} rows={5} placeholder={t('text551')} />
                  </FormControl>
                  <FormMessage className={cn('', 'ea-add-advisory-dialog__value-message')} />
                </FormItem>
              )}
            />
          </div>

          <DialogFooter
            className={cn(
              'flex flex-row flex-wrap items-center justify-end gap-2',
              'ea-add-advisory-dialog__footer',
            )}
          >
            <Button
              className={cn('w-full md:w-auto md:flex-1', 'ea-add-advisory-dialog__footer-button')}
            >
              <FiSend className="mr-2 h-5 w-5" />
              {t('text185')}
            </Button>
          </DialogFooter>
        </Form>
      </form>
    </DialogContent>
  );
}

AddAdvisoryDialog.id = 'ADD_ADVISORY_DIALOG';
